import { routeForNotification } from '../../utils/notificationUtils';

/**
 * Notification Navigation Service
 *
 * Centralno mapiranje push payload-a na in-app rutu:
 *
 *   { type: 'task',  entityId } -> /tasks/:id (ili rola-specifična task ruta)
 *   { type: 'shift', entityId } -> /shift-management
 *
 * Servis ne zna za React. Provajder mu jednom preda `navigate` funkciju i
 * signal da su auth/router spremni. Ako push stigne pre toga (cold start),
 * akcija se čuva kao pending i izvršava se nakon prijave.
 *
 * Za buduće tipove/detaljne rute koristi se `registerRouteResolver` - nema
 * switch/case logike po komponentama.
 */

const isDev = process.env.NODE_ENV !== 'production';

let navigateFn = null;
let navigationReady = false;
let pendingPayload = null;

// Dodatni resolveri za buduće tipove notifikacija (npr. 'resident').
const customResolvers = {};

/**
 * Podrazumevano mapiranje. Za task/shift se koristi postojeća centralna
 * `routeForNotification` logika (uključujući role-specifične task rute).
 */
const DEFAULT_ROUTE_RESOLVERS = {
  task: ({ entityId }) => routeForNotification({ entityType: 'Task', entityId }),
  shift: ({ entityId }) => routeForNotification({ entityType: 'Shift', entityId }),
};

/**
 * Normalizuj Capacitor/backend payload u `{ type, entityId }`.
 * Podržava i slučaj kada je data JSON string (Android FCM može da pošalje
 * string vrednosti) i kada je prosleđen ceo action objekat.
 *
 * @param {*} data
 * @returns {{type: string, entityId: string}|null}
 */
export const normalizeNotificationPayload = (data) => {
  if (!data) return null;

  let source = data;

  if (typeof source === 'string') {
    try {
      source = JSON.parse(source);
    } catch (error) {
      return null;
    }
  }

  if (source?.notification?.data) {
    source = source.notification.data;
  }

  if (!source || typeof source !== 'object') return null;

  const rawType = source.type ?? source.entityType;
  const rawEntityId = source.entityId ?? source.id;

  if (typeof rawType !== 'string' || !rawType.trim()) return null;
  if (rawEntityId === null || rawEntityId === undefined || rawEntityId === '') return null;

  return {
    type: rawType.trim().toLowerCase(),
    entityId: String(rawEntityId),
  };
};

/**
 * Mapiraj normalizovani payload na rutu. Centralna tačka za sve tipove.
 * @param {{type: string, entityId: string}} payload
 * @returns {string|null}
 */
export const resolveNotificationRoute = (payload) => {
  if (!payload?.type || !payload?.entityId) return null;

  const resolver = customResolvers[payload.type] || DEFAULT_ROUTE_RESOLVERS[payload.type];
  if (typeof resolver !== 'function') return null;

  try {
    return resolver(payload) || null;
  } catch (error) {
    console.warn('Push navigacija: mapiranje rute nije uspelo.', error?.message || error);
    return null;
  }
};

/**
 * Registruj resolver za novi tip notifikacije (extension point).
 * @param {string} type - Tip iz payload-a (npr. 'resident')
 * @param {(payload: {type: string, entityId: string}) => string|null} resolver
 * @returns {() => void} Funkcija za uklanjanje resolvera
 */
export const registerNotificationRouteResolver = (type, resolver) => {
  const key = typeof type === 'string' ? type.trim().toLowerCase() : '';
  if (!key || typeof resolver !== 'function') {
    throw new Error('Tip i resolver su obavezni za registraciju rute notifikacije.');
  }

  customResolvers[key] = resolver;
  return () => {
    if (customResolvers[key] === resolver) {
      delete customResolvers[key];
    }
  };
};

// Alias za javni API (i za servisni objekat ispod).
export const registerRouteResolver = registerNotificationRouteResolver;

/**
 * Izvrši navigaciju ako su router i auth spremni; inače sačuvaj akciju.
 * @param {*} data - `pushNotificationActionPerformed.notification.data`
 * @returns {string|null} Ruta ako je navigacija izvršena
 */
export const handleNotificationAction = (data) => {
  const payload = normalizeNotificationPayload(data);

  if (!payload) {
    if (isDev) console.warn('Push navigacija: payload nema type/entityId.');
    return null;
  }

  if (!navigationReady || !navigateFn) {
    // Cold start: app je pokrenuta tapom na notifikaciju pre nego što su
    // auth/router spremni. Ne gubi se - čeka se spreman router.
    pendingPayload = payload;
    if (isDev) console.info('Push navigacija: akcija sačuvana do spremnog rutera.', payload);
    return null;
  }

  const route = resolveNotificationRoute(payload);
  if (!route) {
    if (isDev) console.warn('Push navigacija: nepoznat tip notifikacije.', payload.type);
    return null;
  }

  navigateFn(route);
  return route;
};

/**
 * Izvrši odloženu navigaciju (ako postoji) kada je router spreman.
 * @returns {string|null} Izvršena ruta
 */
export const flushPendingNavigation = () => {
  if (!pendingPayload || !navigationReady || !navigateFn) return null;

  const payload = pendingPayload;
  pendingPayload = null;

  const route = resolveNotificationRoute(payload);
  if (!route) {
    if (isDev) console.warn('Push navigacija: pending akcija nema validnu rutu.', payload.type);
    return null;
  }

  navigateFn(route);
  return route;
};

/**
 * Provajder predaje `navigate` iz react-router-a.
 * @param {Function|null} navigate
 */
export const setNavigationHandler = (navigate) => {
  navigateFn = typeof navigate === 'function' ? navigate : null;
  if (navigateFn && navigationReady) {
    flushPendingNavigation();
  }
};

/**
 * Označi da su auth i router spremni za navigaciju. Kada postane true,
 * pending akcija (cold start) se odmah izvršava.
 * @param {boolean} ready
 */
export const setNavigationReady = (ready) => {
  navigationReady = Boolean(ready);
  if (navigationReady) {
    flushPendingNavigation();
  }
};

/**
 * Reset internog stanja (testovi / eksplicitna promena korisnika).
 */
export const resetNotificationNavigation = () => {
  navigateFn = null;
  navigationReady = false;
  pendingPayload = null;
  Object.keys(customResolvers).forEach((key) => delete customResolvers[key]);
};

const notificationNavigationService = {
  normalizeNotificationPayload,
  resolveNotificationRoute,
  registerRouteResolver: registerNotificationRouteResolver,
  handleNotificationAction,
  flushPendingNavigation,
  setNavigationHandler,
  setNavigationReady,
  reset: resetNotificationNavigation,
};

export default notificationNavigationService;

